/**
 * Parses a prediction job's stored `config_json` into the raw input components shown as chips
 * on the Predictions page.
 *
 * v1 only renders raw components (`_type: 'aminoAcids'` / `_type: 'smiles'`), mirroring the
 * canonical schema-v1 shapes in
 * benchling/model_hub/scientific_models/config/prediction_config_v1.py. Each component carries an
 * optional human-readable `name` and sequence/SMILES `value`. Registry-entity (ID) components are
 * deferred to a later PR.
 *
 * The parser is defensive: missing or legacy configs return an empty array so those rows simply
 * render no chips rather than throwing.
 */

export type PredictionInputType = 'aminoAcids' | 'smiles';

export interface PredictionInput {
  type: PredictionInputType;
  name?: string;
  value?: string;
}

function parseComponent(component: unknown): PredictionInput | null {
  if (component == null || typeof component !== 'object' || Array.isArray(component)) {
    return null;
  }
  const record = component as Record<string, unknown>;
  const type = record._type;
  if (type !== 'aminoAcids' && type !== 'smiles') {
    return null;
  }
  const name = typeof record.name === 'string' && record.name.length > 0 ? record.name : undefined;
  const value = typeof record.value === 'string' && record.value.length > 0 ? record.value : undefined;
  return {
    type,
    ...(name != null ? {name} : {}),
    ...(value != null ? {value} : {}),
  };
}

export function parsePredictionInputs(
  configJson: Record<string, unknown> | null | undefined
): Array<PredictionInput> {
  if (configJson == null) {
    return [];
  }

  // Multimer shape: a `components` array.
  const components = configJson.components;
  if (Array.isArray(components)) {
    return components
      .map((component) => parseComponent(component))
      .filter((input): input is PredictionInput => input != null);
  }

  // Monomer shape: a single `component` object.
  const single = parseComponent(configJson.component);
  return single != null ? [single] : [];
}
